import { useMemo } from 'react';

const DEFAULT_SINGULAR = 'Coin';
const DEFAULT_PLURAL = 'Coins';

/**
 * What this group calls its points, e.g. Mile / Miles.
 *
 * Two labels, not one, because naive pluralizing breaks on real names ("Bucks", "Miles") and would print "1 Coins".
 * Labels are used exactly as the company typed them.
 *
 * Known gap: the public Home page still says "coins". It loads before any group is known, and a branded subdomain
 * skips it (it redirects to login), so only the shared front page shows it.
 *
 * @param {object} source - any object carrying points_label_singular / points_label_plural (a group, a points row)
 * @returns {{
 *   singular: string,
 *   plural: string,
 *   word: (count: number) => string,    // just the label for a count: "Mile" or "Miles"
 *   format: (count: number) => string,  // count and label: "1,240 Miles"
 * }}
 */
const usePointsLabel = (source) =>
  useMemo(() => {
    const singular = source?.points_label_singular || DEFAULT_SINGULAR;
    const plural = source?.points_label_plural || DEFAULT_PLURAL;
    const word = (count) => (Math.floor(Number(count) || 0) === 1 ? singular : plural);
    return {
      singular,
      plural,
      word,
      format: (count) => `${Math.floor(Number(count) || 0).toLocaleString()} ${word(count)}`,
    };
  }, [source?.points_label_singular, source?.points_label_plural]);

export default usePointsLabel;
